import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { learningApi } from '../../api/client';
import { Icon } from '../../components/Icon';

const TYPE_LABEL: Record<string, string> = {
  single: '单选题', multi: '多选题', judge: '判断题', deep: '深度题',
};

// AI 导入的题干可能使用 markdown 图片 ![IMG](url)；统一转成系统标准 [IMG:url]
function normalizeStemMedia(stem: string): string {
  return (stem || '').replace(
    /!\[[^\]]*\]\(\s*([^)\s]+)(?:\s+["'][^"']*["'])?\s*\)/g,
    (_m, url: string) => `[IMG:${String(url).trim()}]`,
  );
}

// 解析题干中的图片 [IMG:url] 与视频 [VIDEO:url] 标记
function renderStemContent(stem: string): React.ReactNode {
  const parts = normalizeStemMedia(stem).split(/\[IMG:([^\]]+)\]|\[VIDEO:([^\]]+)\]/g);
  const nodes: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) nodes.push(<span key={i}>{parts[i]}</span>);
    if (parts[i + 1]) {
      nodes.push(<img key={`img${i}`} src={parts[i + 1]} alt="题目配图" style={{ maxWidth: '100%', margin: '16px 0', borderRadius: 8 }} />);
    }
    if (parts[i + 2]) {
      nodes.push(
        <video
          key={`video${i}`}
          src={parts[i + 2]}
          controls
          playsInline
          preload="none"
          style={{ width: '100%', maxWidth: '100%', margin: '16px 0', borderRadius: 8, background: '#000' }}
        />
      );
    }
  }
  return nodes;
}

// 从错题项中提取题目的 public_id
function getPublicId(item: any): string {
  const q = item.question || item;
  return q.public_id || item.public_id || item.question_public_id || '';
}

export function WrongBookPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    learningApi.getWrongBook()
      .then(data => { setItems(data.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // 开始连续练习：收集所有有 public_id 的错题，跳转第一题
  function startSequentialPractice() {
    const ids = items.map(getPublicId).filter(Boolean);
    if (ids.length === 0) return;
    navigate(`/question/${ids[0]}?mode=wrong&ids=${ids.join(',')}&idx=0`);
  }

  if (loading) return <div className="loading-state"><span className="spinner" /></div>;

  return (
    <div className="page">
      <h1>错题本</h1>
      <p style={{ color: 'var(--gray-500)', marginBottom: 16, fontSize: 13 }}>
        共 {items.length} 道错题 · 建议重点复习
      </p>

      {items.length > 0 && (
        <button
          className="btn-primary"
          onClick={startSequentialPractice}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: 15,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <Icon name="play" size={18} filled color="#fff" /> 开始连续练习（{items.length} 题）
        </button>
      )}

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Icon name="check" size={48} color="var(--gray-300)" filled />
          </div>
          <p className="empty-state-text">暂无错题，继续加油！</p>
        </div>
      ) : (
        <div className="wrong-list">
          {items.map((item, i) => {
            const q = item.question || item;
            const stem = q.stem || item.stem || `题目 ${item.questionId || item.question_id || ''}`;
            const options: string[] = q.options || item.options || [];
            const rawAns = q.answer ?? item.answer;
            const correctSet = new Set<number>(
              Array.isArray(rawAns) ? rawAns : (rawAns != null ? [rawAns] : [])
            );
            const explanation = q.explanation || item.explanation || '';
            const wc = item.wrongCount ?? item.wrong_count ?? 1;
            const streak = item.correctStreak ?? item.correct_streak ?? 0;
            const lwa = item.lastWrongAt ?? item.last_wrong_at ?? '';
            const position = q.position ?? item.position;
            const cardNo = item.cardNo ?? item.card_no ?? position;
            const type = q.type || item.type;
            const publicId = getPublicId(item);

            function handlePractice() {
              // "再练一次"统一进入错题连续练习模式，答完返回错题本
              const ids = items.map(getPublicId).filter(Boolean);
              const curIdx = publicId ? ids.indexOf(publicId) : -1;
              const startIdx = curIdx >= 0 ? curIdx : 0;
              if (ids.length > 0) {
                navigate(`/question/${ids[startIdx]}?mode=wrong&ids=${ids.join(',')}&idx=${startIdx}`);
              } else if (cardNo != null) {
                navigate(`/q/${cardNo}`);
              }
            }

            return (
              <div key={i} className="wrong-item">
                {type && (
                  <span className={`type-badge type-badge-${type}`}>
                    {TYPE_LABEL[type] || type}
                  </span>
                )}
                <div className="wrong-stem">{renderStemContent(stem)}</div>

                {options.length > 0 && (
                  <div className="quiz-options" style={{ marginTop: 12 }}>
                    {options.map((opt, oi) => {
                      const isCorrect = correctSet.has(oi);
                      const cls = 'quiz-option' + (isCorrect ? ' correct' : '');
                      return (
                        <div key={oi} className={cls} style={{ cursor: 'default' }}>
                          <span className="opt-letter">{String.fromCharCode(65 + oi)}</span>
                          <span className="opt-content">{opt}</span>
                          {isCorrect && <span className="opt-icon"><Icon name="check" size={16} filled color="#059669" /></span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {explanation && (
                  <div className="wrong-explanation">
                    <Icon name="book-open" size={14} color="var(--color-primary)" />
                    <span>{explanation}</span>
                  </div>
                )}

                <div className="wrong-meta">
                  <span className="wrong-meta-item">
                    <Icon name="close" size={13} color="var(--color-danger)" filled />
                    错误 {wc} 次
                  </span>
                  <span className="wrong-meta-item" style={{ color: streak > 0 ? '#059669' : undefined }}>
                    <Icon name="check" size={13} color={streak > 0 ? '#059669' : 'var(--gray-400)'} />
                    连续答对 {streak}/2
                  </span>
                  {lwa && (
                    <span className="wrong-meta-item">
                      <Icon name="clock" size={13} color="var(--gray-400)" />
                      {lwa}
                    </span>
                  )}
                </div>

                {(cardNo != null || publicId) && (
                  <div style={{ marginTop: 10, textAlign: 'right' }}>
                    <button className="btn-primary" style={{ padding: '6px 14px', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={handlePractice}>
                      <Icon name="refresh" size={14} /> 再练一次
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
